import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import LanguageFlag from '../common/LanguageFlag';
import PrimaryActionButton from '../common/PrimaryActionButton';
import ScrollableDialog from '../common/ScrollableDialog';
import SwitchField from '../common/SwitchField';
import ConsentFormsSummary from '../consent-forms/ConsentFormsSummary';
import VatClassificationFields from '../common/VatClassificationFields';
import { readVatClassification, validateVatClassification } from '../../utils/vatClassification';
import { getLocalizedValue, SUPPORTED_LANGUAGE_CODES } from '../../constants/Languages';
import { BRAND } from '../../theme/brandTokens';
import { serviceAllowsPlanOrPackage } from '../../utils/serviceValueFormat';

const MEMBERSHIP_BILLING_INTERVAL = {
  Monthly: 'Monthly',
};

const MEMBERSHIP_ENTITLEMENT_TYPE = {
  UnlimitedUse: 'UnlimitedUse',
  FixedAllowancePerPeriod: 'FixedAllowancePerPeriod',
};

const buildLocalizedFields = (supportedLanguages, values = {}) => (
  Object.fromEntries(supportedLanguages.map((language) => [language, values?.[language] || '']))
);

const buildInitialPlan = (supportedLanguages, plan) => ({
  id: plan?.id ?? null,
  name: buildLocalizedFields(supportedLanguages, plan?.name),
  price: plan?.price ?? '',
  billingInterval: plan?.billingInterval || MEMBERSHIP_BILLING_INTERVAL.Monthly,
  entitlementType: plan?.entitlementType || MEMBERSHIP_ENTITLEMENT_TYPE.UnlimitedUse,
  allowanceQuantity: plan?.allowanceQuantity ?? '',
  allowCustomerCancellation: plan?.allowCustomerCancellation ?? true,
  commitmentMonths: plan?.commitmentMonths ?? '',
  serviceIds: Array.isArray(plan?.serviceIds) ? [...plan.serviceIds] : [],
  teamMemberIds: Array.isArray(plan?.teamMemberIds) ? [...plan.teamMemberIds] : [],
  ...readVatClassification(plan),
});

const buildTextFieldSx = (hasError = false) => ({
  width: '100%',
  padding: '12px 16px',
  border: `2px solid ${hasError ? '#f44336' : BRAND.line}`,
  borderRadius: '8px',
  fontSize: '16px',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.3s ease',
  backgroundColor: '#fff',
  boxSizing: 'border-box',
  lineHeight: 'normal',
  minHeight: 48,
  '&:focus': {
    borderColor: BRAND.accent,
  },
});

const buildSelectFieldSx = (hasError = false) => ({
  height: '48px !important',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: hasError ? '#f44336' : BRAND.line,
    borderWidth: '2px !important',
    borderRadius: '8px !important',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: `${BRAND.accent} !important`,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: `${BRAND.accent} !important`,
    borderWidth: '2px !important',
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 16px',
    fontSize: '16px',
  },
});

const MembershipPlanEditorDialog = ({
  open,
  mode,
  plan,
  supportedLanguages,
  currency,
  services,
  teamMembers = [],
  consentForms = [],
  saving,
  onClose,
  onSave,
}) => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState(() => buildInitialPlan(supportedLanguages, plan));
  const [errors, setErrors] = useState({});
  const sortedLanguages = useMemo(
    () => [...supportedLanguages].sort(
      (a, b) => SUPPORTED_LANGUAGE_CODES.indexOf(a) - SUPPORTED_LANGUAGE_CODES.indexOf(b)
    ),
    [supportedLanguages]
  );
  const [activeLanguage, setActiveLanguage] = useState(() => supportedLanguages[0] || 'en');

  useEffect(() => {
    if (open) {
      setFormData(buildInitialPlan(supportedLanguages, plan));
      setErrors({});
    }
  }, [open, plan, supportedLanguages]);

  useEffect(() => {
    if (!open || sortedLanguages.length === 0) {
      return;
    }

    setActiveLanguage(sortedLanguages[0]);
  }, [open, sortedLanguages]);

  useEffect(() => {
    if (sortedLanguages.length === 0 || sortedLanguages.includes(activeLanguage)) {
      return;
    }

    setActiveLanguage(sortedLanguages[0]);
  }, [activeLanguage, sortedLanguages]);

  const toggleService = (serviceId) => {
    setFormData((previous) => {
      const currentIds = new Set(previous.serviceIds);
      if (currentIds.has(serviceId)) {
        currentIds.delete(serviceId);
      } else {
        currentIds.add(serviceId);
      }

      return {
        ...previous,
        serviceIds: [...currentIds],
      };
    });
  };

  const toggleTeamMember = (teamMemberId) => {
    setFormData((previous) => {
      const currentIds = new Set(previous.teamMemberIds);
      if (currentIds.has(teamMemberId)) {
        currentIds.delete(teamMemberId);
      } else {
        currentIds.add(teamMemberId);
      }

      return {
        ...previous,
        teamMemberIds: [...currentIds],
      };
    });
  };

  const handleSubmit = () => {
    const nextErrors = {};
    const hasAllNames = supportedLanguages.every((language) => formData.name?.[language]?.trim());
    const parsedPrice = Number(formData.price);
    const isFixedAllowance = formData.entitlementType === MEMBERSHIP_ENTITLEMENT_TYPE.FixedAllowancePerPeriod;
    const parsedAllowance = Number.parseInt(formData.allowanceQuantity, 10);
    const parsedCommitment = formData.commitmentMonths === '' || formData.commitmentMonths == null
      ? null
      : Number.parseInt(formData.commitmentMonths, 10);

    if (!hasAllNames) {
      nextErrors.name = 'membershipPlansManagement.validation.nameRequired';
    }

    if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) {
      nextErrors.price = 'membershipPlansManagement.validation.priceRequired';
    }

    if (isFixedAllowance && (!Number.isInteger(parsedAllowance) || parsedAllowance <= 0)) {
      nextErrors.allowanceQuantity = 'membershipPlansManagement.validation.allowanceRequired';
    }

    if (parsedCommitment != null && (!Number.isInteger(parsedCommitment) || parsedCommitment <= 0)) {
      nextErrors.commitmentMonths = 'membershipPlansManagement.validation.commitmentRequired';
    }

    const eligibleServiceIds = (formData.serviceIds || []).filter((serviceId) => {
      const service = services.find((item) => item.id === serviceId);
      return service && serviceAllowsPlanOrPackage(service);
    });

    if (!eligibleServiceIds.length) {
      nextErrors.serviceIds = 'membershipPlansManagement.validation.servicesRequired';
    }

    Object.assign(nextErrors, validateVatClassification(formData));

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    onSave({
      ...formData,
      price: parsedPrice,
      name: buildLocalizedFields(supportedLanguages, formData.name),
      allowanceQuantity: isFixedAllowance ? parsedAllowance : null,
      commitmentMonths: parsedCommitment,
      teamMemberIds: formData.teamMemberIds,
      serviceIds: (formData.serviceIds || []).filter((serviceId) => {
        const service = services.find((item) => item.id === serviceId);
        return service && serviceAllowsPlanOrPackage(service);
      }),
    });
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      title={mode === 'edit' ? t('membershipPlansManagement.dialog.editTitle') : t('membershipPlansManagement.dialog.createTitle')}
      dividers
      showCloseButton
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{
        flexShrink: 0,
        justifyContent: 'space-between',
      }}
      actions={(
        <>
          <Button onClick={onClose} disabled={saving} color="inherit">
            {t('common.cancel')}
          </Button>
          <PrimaryActionButton onClick={handleSubmit} disabled={saving}>
            {saving
              ? t('common.saving')
              : mode === 'edit'
                ? t('membershipPlansManagement.dialog.saveEdit')
                : t('membershipPlansManagement.dialog.saveCreate')}
          </PrimaryActionButton>
        </>
      )}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: errors.name ? 'error.main' : 'divider',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={activeLanguage}
            onChange={(_, nextLanguage) => setActiveLanguage(nextLanguage)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              backgroundColor: '#f8f9fa',
              '& .MuiTab-root': {
                color: '#666',
                fontWeight: 600,
                textTransform: 'none',
                minHeight: 48,
              },
              '& .Mui-selected': {
                color: BRAND.accentText,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: BRAND.accent,
              },
            }}
          >
            {sortedLanguages.map((language) => (
              <Tab
                key={language}
                value={language}
                label={(
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                    <LanguageFlag languageCode={language} size="1rem" />
                    <Typography variant="body2">{t(`common.languages.${language}`)}</Typography>
                  </Box>
                )}
              />
            ))}
          </Tabs>

          <Box sx={{ p: 2.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {`${t('membershipPlansManagement.fields.name')} (${t(`common.languages.${activeLanguage}`)}) *`}
            </Typography>
            <Box
              component="input"
              type="text"
              aria-label={`${t('membershipPlansManagement.fields.name')} (${t(`common.languages.${activeLanguage}`)})`}
              value={formData.name?.[activeLanguage] || ''}
              onChange={(event) => setFormData((previous) => ({
                ...previous,
                name: {
                  ...previous.name,
                  [activeLanguage]: event.target.value,
                },
              }))}
              sx={buildTextFieldSx(Boolean(errors.name))}
            />
            {errors.name ? (
              <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
                {t(errors.name)}
              </Typography>
            ) : null}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {`${t('membershipPlansManagement.fields.price')} (${currency}) *`}
            </Typography>
            <Box
              component="input"
              type="number"
              aria-label={`${t('membershipPlansManagement.fields.price')} (${currency})`}
              value={formData.price}
              onChange={(event) => setFormData((previous) => ({ ...previous, price: event.target.value }))}
              min={0.01}
              step={0.01}
              sx={buildTextFieldSx(Boolean(errors.price))}
            />
            {errors.price ? (
              <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
                {t(errors.price)}
              </Typography>
            ) : null}
          </Box>

          <VatClassificationFields
            idPrefix="membership-plan-vat"
            value={formData}
            errors={errors}
            onChange={(next) => setFormData((previous) => ({ ...previous, ...next }))}
          />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {t('membershipPlansManagement.fields.billingInterval')}
            </Typography>
            <FormControl fullWidth>
              <Select
                value={formData.billingInterval}
                disabled
                MenuProps={{ disableScrollLock: true }}
                sx={buildSelectFieldSx()}
              >
                <MenuItem value={MEMBERSHIP_BILLING_INTERVAL.Monthly}>
                  {t('membershipPlansManagement.billingInterval.monthly')}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {t('membershipPlansManagement.fields.entitlementType')}
            </Typography>
            <FormControl fullWidth>
              <Select
                value={formData.entitlementType}
                onChange={(event) => setFormData((previous) => ({
                  ...previous,
                  entitlementType: event.target.value,
                  allowanceQuantity: event.target.value === MEMBERSHIP_ENTITLEMENT_TYPE.UnlimitedUse ? '' : previous.allowanceQuantity,
                }))}
                MenuProps={{ disableScrollLock: true }}
                sx={buildSelectFieldSx()}
              >
                <MenuItem value={MEMBERSHIP_ENTITLEMENT_TYPE.UnlimitedUse}>
                  {t('membershipPlansManagement.entitlement.unlimited')}
                </MenuItem>
                <MenuItem value={MEMBERSHIP_ENTITLEMENT_TYPE.FixedAllowancePerPeriod}>
                  {t('membershipPlansManagement.entitlement.fixedAllowance')}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {formData.entitlementType === MEMBERSHIP_ENTITLEMENT_TYPE.FixedAllowancePerPeriod ? (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {`${t('membershipPlansManagement.fields.allowanceQuantity')} *`}
              </Typography>
              <Box
                component="input"
                type="number"
                aria-label={t('membershipPlansManagement.fields.allowanceQuantity')}
                value={formData.allowanceQuantity}
                onChange={(event) => setFormData((previous) => ({ ...previous, allowanceQuantity: event.target.value }))}
                min={1}
                step={1}
                sx={buildTextFieldSx(Boolean(errors.allowanceQuantity))}
              />
              {errors.allowanceQuantity ? (
                <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
                  {t(errors.allowanceQuantity)}
                </Typography>
              ) : null}
            </Box>
          ) : null}

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {t('membershipPlansManagement.fields.commitmentMonths')}
            </Typography>
            <Box
              component="input"
              type="number"
              aria-label={t('membershipPlansManagement.fields.commitmentMonths')}
              value={formData.commitmentMonths}
              onChange={(event) => setFormData((previous) => ({ ...previous, commitmentMonths: event.target.value }))}
              min={1}
              step={1}
              placeholder={t('membershipPlansManagement.fields.commitmentMonthsPlaceholder')}
              sx={buildTextFieldSx(Boolean(errors.commitmentMonths))}
            />
            {errors.commitmentMonths ? (
              <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
                {t(errors.commitmentMonths)}
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {t('membershipPlansManagement.fields.commitmentMonthsHelp')}
              </Typography>
            )}
          </Box>

          <SwitchField
            containerSx={{ gridColumn: '1 / -1', pl: 0, pr: 1 }}
            checked={Boolean(formData.allowCustomerCancellation)}
            onChange={(event) => setFormData((previous) => ({
              ...previous,
              allowCustomerCancellation: event.target.checked,
            }))}
            label={t('membershipPlansManagement.fields.allowCustomerCancellation')}
            helpText={t('membershipPlansManagement.fields.allowCustomerCancellationHelp')}
          />
        </Box>

        <Box
          sx={{
            border: '1px solid',
            borderColor: errors.serviceIds ? 'error.main' : 'divider',
            borderRadius: 2,
            p: 2.5,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {t('membershipPlansManagement.fields.includedServices')} *
          </Typography>
          {services.some((service) => !serviceAllowsPlanOrPackage(service)) ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              {t('servicesManagement.planOrPackageLocked')}
            </Typography>
          ) : null}
          {services.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('membershipPlansManagement.fields.noServicesAvailable')}
            </Typography>
          ) : (
            <FormGroup>
              {services.map((service) => {
                const locked = !serviceAllowsPlanOrPackage(service);
                return (
                <FormControlLabel
                  key={service.id}
                  control={(
                    <Checkbox
                      checked={!locked && formData.serviceIds.includes(service.id)}
                      onChange={() => toggleService(service.id)}
                      disabled={locked}
                    />
                  )}
                  label={getLocalizedValue(service.name, i18n.language, undefined, t('membershipPlansManagement.card.unnamed'))}
                />
                );
              })}
            </FormGroup>
          )}
          {errors.serviceIds ? (
            <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
              {t(errors.serviceIds)}
            </Typography>
          ) : null}
        </Box>

        <ConsentFormsSummary
          id="membership-plan-consent-forms"
          serviceIds={formData.serviceIds}
          services={services}
          consentForms={consentForms}
        />

        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 2.5,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
            {t('membershipPlansManagement.fields.includedProfessionals')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            {t('membershipPlansManagement.fields.includedProfessionalsHelp')}
          </Typography>
          {teamMembers.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('membershipPlansManagement.fields.noProfessionalsAvailable')}
            </Typography>
          ) : (
            <FormGroup>
              {teamMembers.map((member) => (
                <FormControlLabel
                  key={member.id}
                  control={(
                    <Checkbox
                      checked={formData.teamMemberIds.includes(member.id)}
                      onChange={() => toggleTeamMember(member.id)}
                    />
                  )}
                  label={member.name || member.id}
                />
              ))}
            </FormGroup>
          )}
        </Box>
      </Box>
    </ScrollableDialog>
  );
};

export default MembershipPlanEditorDialog;
